import { Link } from 'react-router-dom';
import { Pharmacy, PHARMACY_SERVICES } from '@/mocks/pharmacies';

interface PharmacyCardProps {
  pharmacy: Pharmacy;
  isSelected: boolean;
  onSelect: () => void;
}

const PharmacyCard = ({ pharmacy: p, isSelected, onSelect }: PharmacyCardProps) => {
  const accentColor = p.isOpen ? '#39ff14' : '#ff2d78';

  return (
    <div
      onClick={onSelect}
      className="rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 group"
      style={{
        border: isSelected ? '1px solid rgba(255,45,120,0.5)' : '1px solid rgba(255,255,255,0.07)',
        background: isSelected ? 'rgba(255,45,120,0.04)' : 'rgba(255,255,255,0.02)',
        boxShadow: isSelected ? '0 0 24px rgba(255,45,120,0.1)' : 'none',
      }}
      onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.15)'; }}
      onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)'; }}
    >
      {/* Top accent */}
      {isSelected && (
        <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, #ff2d78, #00f5ff, transparent)' }} />
      )}

      {/* Image */}
      <div className="relative h-36 overflow-hidden">
        <img
          src={p.image}
          alt={p.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(5,5,8,0.1), rgba(5,5,8,0.7))' }} />

        {/* Status badge */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[10px] font-bold"
          style={{ background: p.isOpen ? 'rgba(57,255,20,0.2)' : 'rgba(255,45,120,0.2)', border: `1px solid ${accentColor}50`, color: accentColor, backdropFilter: 'blur(8px)' }}
        >
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: accentColor, boxShadow: `0 0 4px ${accentColor}` }} />
          {p.is24h ? '24/7' : p.isOpen ? 'Открыто' : 'Закрыто'}
        </div>

        {/* Distance */}
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px]"
          style={{ background: 'rgba(5,5,8,0.7)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)' }}
        >
          <i className="ri-map-pin-line" style={{ color: '#ff2d78' }} />
          {p.distance}
        </div>

        {/* Drive-thru badge */}
        {p.hasDriveThru && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[9px]"
            style={{ background: 'rgba(240,255,0,0.15)', border: '1px solid rgba(240,255,0,0.3)', color: '#f0ff00', backdropFilter: 'blur(8px)' }}
          >
            <i className="ri-car-line" />
            Drive-thru
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-orbitron font-bold text-sm text-white leading-tight">{p.name}</h3>
          <div className="flex items-center gap-1 shrink-0">
            <i className="ri-star-fill text-[11px]" style={{ color: '#f0ff00' }} />
            <span className="font-orbitron font-bold text-xs" style={{ color: '#f0ff00' }}>{p.rating}</span>
          </div>
        </div>

        {/* Address + metro */}
        <div className="flex items-start gap-1.5 mb-2">
          <i className="ri-map-pin-2-line text-xs mt-0.5 shrink-0" style={{ color: 'rgba(255,255,255,0.3)' }} />
          <p className="font-mono text-[11px] text-white/45 leading-snug">{p.address}</p>
        </div>
        <div className="flex items-center gap-1.5 mb-3">
          <div className="w-3 h-3 rounded-full shrink-0 flex items-center justify-center" style={{ background: p.metroColor }}>
            <span className="text-white font-bold" style={{ fontSize: '7px' }}>М</span>
          </div>
          <span className="font-mono text-[11px] text-white/45">{p.metro}</span>
          <span className="font-mono text-[10px] text-white/25 ml-auto">{p.hours}</span>
        </div>

        {/* Services */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {p.services.slice(0, 4).map(svcId => {
            const svc = PHARMACY_SERVICES.find(s => s.id === svcId);
            if (!svc) return null;
            return (
              <div
                key={svcId}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[9px]"
                style={{ background: `${svc.color}10`, border: `1px solid ${svc.color}25`, color: svc.color }}
              >
                <i className={`${svc.icon} text-[9px]`} />
                {svc.label}
              </div>
            );
          })}
          {p.services.length > 4 && (
            <div className="flex items-center px-2 py-0.5 rounded-full font-mono text-[9px]"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.35)' }}
            >
              +{p.services.length - 4}
            </div>
          )}
        </div>

        {/* Reviews + CTA */}
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] text-white/30">{p.reviews} отзывов</span>
          <div className="flex items-center gap-2">
            <div
              onClick={e => e.stopPropagation()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[11px] cursor-pointer transition-all duration-200"
              style={isSelected
                ? { background: '#ff2d78', color: '#fff' }
                : { border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }
              }
              onClick={onSelect}
            >
              <i className={isSelected ? 'ri-eye-line' : 'ri-map-pin-line'} />
              {isSelected ? 'На карте' : 'На карте'}
            </div>
            <Link
              to={`/pharmacies/${p.id}`}
              onClick={e => e.stopPropagation()}
              className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[11px] cursor-pointer transition-all duration-200"
              style={{ border: '1px solid rgba(0,245,255,0.3)', color: '#00f5ff' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,245,255,0.08)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <i className="ri-arrow-right-line" />
              Страница
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PharmacyCard;
